import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  api,
  type Project,
  type SiteSettings,
  type Category,
  type PortfolioView,
} from "../lib/api";
import { ProjectCard } from "../components/ProjectCard";
import { usePageTitle } from "../lib/pageTitle";
import "./HomePage.scss";

export function HomePage() {
  const [site, setSite] = useState<SiteSettings | null>(null);
  const [featured, setFeatured] = useState<Project[]>([]);
  const [recent, setRecent] = useState<Project[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [views, setViews] = useState<PortfolioView[]>([]);

  usePageTitle(null); // site name only

  useEffect(() => {
    Promise.all([
      api.site(),
      api.featured().catch(() => [] as Project[]),
      // Fetch extra so we can exclude featured IDs from Recent
      api.projects({ sort: "date", perPage: 18 }),
      api.categories(),
      api.views().catch(() => [] as PortfolioView[]),
    ]).then(([s, f, r, c, v]) => {
      setSite(s);
      setFeatured(f);
      const featuredIds = new Set(f.map((p) => p.id));
      setRecent(r.data.filter((p) => !featuredIds.has(p.id)).slice(0, 6));
      setCategories(c);
      setViews(v);
    });
  }, []);

  const heroSub = site?.heroSubtitle || "3D · Design · Motion · Craft";
  // Hide empty categories (avoid dead-end chips); keep curated views
  const liveCategories = categories.filter(
    (c) => typeof c.projectCount !== "number" || c.projectCount > 0
  );
  const categoryById = new Map(categories.map((c) => [c.id, c]));
  const categoryBySlug = new Map(categories.map((c) => [c.slug, c]));
  // Avoid advertising a view that collides with a category slug, or whose
  // prioritized categories all have zero published projects (e.g. AI with empty cats)
  const liveViews = views.filter((v) => {
    if (categoryBySlug.has(v.slug)) return false;
    const prios = [...(v.categoryPriorities || [])].sort(
      (a, b) => a.priority - b.priority
    );
    if (prios.length === 0) return true;
    // Only promote a view when its *primary* category has published work
    // (avoids AI view dumping industrial projects via showOthers)
    const primary = prios[0];
    const cat =
      (primary.category?.id && categoryById.get(primary.category.id)) ||
      (primary.category?.slug && categoryBySlug.get(primary.category.slug)) ||
      (primary.categoryId ? categoryById.get(primary.categoryId) : undefined);
    return (cat?.projectCount ?? 0) > 0;
  });
  const contactEmail = site?.social?.email || "jmartin@jmartgraphix.com";

  return (
    <div className="home page-enter">
      <section className="home-hero">
        <div className="container">
          <img
            src="/logo.png"
            alt="jmartgraphix"
            className="home-hero__logo"
            width={200}
            height={200}
            decoding="async"
          />
          <h1 className="sr-only">jmartgraphix</h1>
          <p className="home-hero__sub">{heroSub}</p>
          <p className="home-hero__about">
            {site?.about ||
              "A curated collection of 3D modeling, CAD, animation, and visual design."}
          </p>
          <div className="home-hero__actions">
            <Link to="/portfolio" className="btn btn--primary">
              View portfolio
            </Link>
            <Link to="/resume" className="btn btn--ghost">
              Resume
            </Link>
            <a className="btn btn--ghost" href={`mailto:${contactEmail}`}>
              Hire / contact
            </a>
          </div>
        </div>
      </section>

      <section className="home-cats container">
        <div className="home-cats__list">
          {liveCategories.map((c) => {
            const countLabel =
              typeof c.projectCount === "number"
                ? `${c.projectCount} project${c.projectCount === 1 ? "" : "s"}`
                : undefined;
            return (
              <Link
                key={c.id}
                to={`/portfolio/${c.slug}`}
                className="home-cats__chip"
                aria-label={countLabel ? `${c.name}, ${countLabel}` : c.name}
              >
                {c.name}
                {typeof c.projectCount === "number" && (
                  <span aria-hidden="true">{c.projectCount}</span>
                )}
              </Link>
            );
          })}
          {liveViews.map((v) => (
            <Link
              key={v.id}
              to={`/portfolio/${v.slug}`}
              className="home-cats__chip home-cats__chip--view"
            >
              {v.name}
            </Link>
          ))}
        </div>
      </section>

      {featured.length > 0 && (
        <section className="container home-section">
          <div className="home-section__head">
            <h2>Featured</h2>
            <Link to="/portfolio">All work →</Link>
          </div>
          <div className="project-grid">
            {featured.map((p, i) => (
              <ProjectCard key={p.id} project={p} index={i} />
            ))}
          </div>
        </section>
      )}

      <section className="container home-section">
        <div className="home-section__head">
          <h2>Recent</h2>
        </div>
        {recent.length === 0 ? (
          <p className="home-empty">
            Projects will appear here once published.
          </p>
        ) : (
          <div className="project-grid">
            {recent.map((p, i) => (
              <ProjectCard key={p.id} project={p} index={i} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
